/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo, useState, useRef } from "react";
import { Grid } from "@mui/material";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
// prop-types is a library for typechecking of props
import PropTypes from "prop-types";
import SearchIcon from "@mui/icons-material/Search";
// react-table components
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";
import SoftInput from "components/SoftInput";
import SoftPagination from "components/SoftPagination";
import SoftButton from "components/SoftButton";
// Soft UI Dashboard PRO React example components
import DataTableHeadCell from "./PortfolioStockDataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import Popup from "layouts/applications/popup/Popup";
import { getTickerList } from "layouts/applications/data-tables/data/getData";
import { SearchSymbolInput, ActionDropdown, TableRemarks } from "components/CustomComponents";
import axiosDefault from "layouts/authentication/api/axios";
import { useDispatch, useSelector } from "react-redux";
import { buyStock as adjustCapitalOnBuyStock, sellStock as adjustCapitalOnSellStock } from "reducers/portfolio/capital";
import { buyStock, sellStock } from "reducers/portfolio/stock";
import DashboardLink from "components/etc/DashboardLink";
import { isNumber } from "lodash";
import moment from "moment";

function PortfolioStockTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  loading,
  isDividend,
  exchange,
  setExchange,
  optionType,
  fullData,
  modifyTable,
}) {
  const defaultValue = entriesPerPage.defaultValue ? entriesPerPage.defaultValue : 10;
  const entries = entriesPerPage.entries ? entriesPerPage.entries : [5, 10, 15, 20, 25];
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows ?? [], [table]);
  // const [data,setData]= useState(table.rows)

  const [isModalOpen, setModal] = useState(false);
  const [ratio, setRatio] = useState();
  const [currentOption, setOption] = useState({});
  const [CurrentOptionType, setOptionType] = useState("");
  const [filteredData, setFiltered] = useState();
  const [stockCode, setStockCode] = useState("");
  const navigate = useNavigate();
  const [stockPopup, toggleStockPopup] = useState(false);
  const [actionPopup, toggleActionPopup] = useState(false);
  const [capitalPopup, toggleCapitalPopup] = useState(false);
  const [isConfirmed, setConfirmed] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [symbolObject, setSymbolObject] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [stockToClose, setStockToClose] = useState(null);

  const dispatch = useDispatch();
  const initialCapital = useSelector((state) => state.userCapital.capital);
  const cashFlow = useSelector((state) => state.userCapital.cashFlow);

  //states for stock popup
  const [inputText, setInputText] = useState({
    tradeDate: "",
    tradePrice: "",
    quantity: "",
    capital: 0,
    symbol: "",
    remarks: "none",
    action: "Hold",
  });

  //stock type
  const [stockType, setStockType] = useState(null);
  const openStock = Boolean(stockType);
  const [currentStock, setCurrentStock] = useState("CHOOSE STOCK TYPE");

  //actionType
  const [actionType, setActionType] = useState(null);
  const openAction = Boolean(actionType);
  const [currentAction, setCurrentAction] = useState();
  const lastIndexOfInputColumn = 5;

  //monitor scrolling
  const [scrollY, setScrollY] = useState(0);
  const scrollableRef = useRef(null);
  function handleScroll() {
    const scrollPos = scrollableRef.current.scrollTop;
    setScrollY(scrollPos);
    console.log(scrollY);
  }

  const sendStockInfo = async () => {
    uploadStockInfo(inputText);
    cancelCurrentInfo();
  };

  const uploadStockInfo = async (objectToUpload) => {
    objectToUpload = { ...objectToUpload, name: inputText.name, capital: objectToUpload.capital.toFixed(2).toString() };
    // console.log(objectToUpload)
    try {
      setIsLoading(true);
      const res = await axiosDefault().post("portfolio/buy-stocks", objectToUpload);
      if (res.status === 200) {
        dispatch(buyStock(res.data.data));
        dispatch(adjustCapitalOnBuyStock(objectToUpload.capital));
      }
      setIsLoading(false);
    } catch (error) {
      console.log(error);
      console.log("error uploading stock info");
      setIsLoading(false);
    }
  };

  const markStockClosed = async () => {
    setIsLoading(true);
    const stock = { ...stockToClose };
    stock.tradeDate = stock.closeDate ? stock.closeDate : moment().format("YYYY-MM-DD");
    if (stockToClose.yourClosingPrice) {
      stock.tradePrice = stockToClose.yourClosingPrice;
      stock.pAndL = (stockToClose.yourClosingPrice * stockToClose.quantity - stockToClose.capital).toFixed(2);
      stock.pAndLPercentage = ((stock.pAndL / stockToClose.capital) * 100).toFixed(2)
    }
    const res = await axiosDefault().post("portfolio/sell-stocks", stock);
    if (res.status === 200) {
      dispatch(sellStock(res.data.data));
      dispatch(adjustCapitalOnSellStock(res.data.data));
    }
    setIsLoading(false);
    cancelCurrentInfo();
  };

  const initialisePopupInputs = () => {
    const currentText = {};
    let numberOfInputs = lastIndexOfInputColumn;
    for (let i = 0; i < numberOfInputs; i++) {
      let name = table.columns[i].accessor;
      if (table.columns[i].type === "date") {
        const today = new Date();
        currentText[name] = today.toLocaleDateString("en-US");
      } else {
        currentText[name] = "";
      }
    }
    setInputText(currentText);
  };
  const cancelCurrentInfo = () => {
    setConfirmed(false);
    initialisePopupInputs();
    setCurrentStock("CHOOSE STOCK TYPE");

    setStockToClose(null);
    toggleActionPopup(false);

    toggleStockPopup(false);
    setModal(false);
    setErrorMessage("");
  };

  const isFieldsValid = async () => {
    console.log(inputText);
    if (inputText.symbol === "") {
      setErrorMessage("Invalid Symbol");
      return false;
    } else if (inputText.tradeDate === "" || isNaN(new Date(inputText.tradeDate))) {
      setErrorMessage("please enter this trade's date");
      return false;
    } else if (inputText.tradePrice === 0 || inputText.tradePrice === "") {
      setErrorMessage("please enter a trade price");
      return false;
    } else if (inputText.quantity === 0 || inputText.quantity === "") {
      setErrorMessage("Please enter a quantity");
      return false;
    } else if (inputText.tradePrice * inputText.quantity > cashFlow) {
      setErrorMessage("You have insufficient capital, you need to\nput in more capital.");
      return false;
    }

    let fetchedObject = await getTickerList(inputText["symbol"]);
    if (fetchedObject.data.length === 0) {
      setErrorMessage("Invalid Symbol: " + inputText["symbol"]);
      return false;
    } else {
      setErrorMessage("");
      return true;
    }
  };
  const confirmButton = async () => {
    if (await isFieldsValid()) {
      setConfirmed(!isConfirmed);
    }
  };
  const handleSymbolData = async (symbol) => {
    console.log(symbol);
    console.log("fetching symbol obj below");
    const fetchedSymbolObject = await getTickerList(symbol);
    setSymbolObject(fetchedSymbolObject);
    console.log(symbolObject);
  };
  function handleStockChange(event) {
    const name = event.target.name;
    const value = event.target.value;
    setInputText((prevValue) => {
      if (name === "tradePrice") {
        if (value * prevValue.quantity > cashFlow) {
          setErrorMessage("You have insufficient capital, you need to\nput in more capital.");
        } else {
          setErrorMessage("");
        }
        return { ...prevValue, tradePrice: value, capital: value * prevValue.quantity };
      } else if (name === "quantity") {
        if (value * prevValue.tradePrice > cashFlow) {
          setErrorMessage("You have insufficient capital, you need to\nput in more capital.");
        } else {
          setErrorMessage("");
        }
        return { ...prevValue, quantity: value, capital: value * prevValue.tradePrice };
      } else if (name === "symbol") {
        handleSymbolData(value);
        return { ...prevValue, [name]: value };
      } else {
        return { ...prevValue, [name]: value };
      }
    });
  }

  const closeStock = (event) => {
    const name = event.target.textContent;
    if (name !== "") {
      setCurrentStock(name);
      setInputText({ ...inputText, stockType: name });
    }
    setStockType(null);
  };

  const closeAction = (event) => {
    const name = event.target.textContent;
    if (name !== "") {
      //work on this logic where index
      setCurrentStock(name);
      setInputText({ ...inputText, stockType: name });
    }
    setActionType(null);
  };
  const handleClick = (event) => {
    const name = event.currentTarget.id;
    if (name === "stock-button") {
      setStockType(event.currentTarget);
    } else if (name === "action-button") {
      setActionType(event.currentTarget);
    }
    // if (name === )
  };
  const tableInstance = useTable(
    { columns, data, initialState: { pageIndex: 0 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,

    state: { pageIndex, pageSize, globalFilter },
  } = tableInstance;

  // Set the default value for the entries per page when component mounts
  // useEffect(() => setPageSize(defaultValue || 10), [defaultValue]);

  // Set the entries per page value based on the select value
  const setEntriesPerPage = ({ value }) => setPageSize(value);

  const parseData = (name, data) => {
    const theElement = data.find((element) => element.Symbol === name);

    setFiltered(theElement);
  };

  // Render the paginations
  const renderPagination = pageOptions.map((option) => (
    <SoftPagination
      item
      key={option}
      onClick={() => gotoPage(Number(option))}
      active={pageIndex === option}
      className={pageIndex === option ? "bbvRed" : ""}
    >
      {option + 1}
    </SoftPagination>
  ));

  // Handler for the input to set the pagination index
  const handleInputPagination = ({ target: { value } }) =>
    value > pageOptions.length || value < 0 ? gotoPage(0) : gotoPage(Number(value));

  // Customized page options starting from 1
  const customizedPageOptions = pageOptions.map((option) => option + 1);

  // Setting value for the pagination input
  const handleInputPaginationValue = ({ target: value }) => gotoPage(Number(value.value - 1));

  // Search input value state
  const [search, setSearch] = useState(globalFilter);

  // Search input state handle
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    let sortedValue;

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  // Setting the entries starting point
  const entriesStart = pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1;

  // Setting the entries ending point
  let entriesEnd;

  if (pageIndex === 0) {
    entriesEnd = Math.min(rows.length, pageSize);
  } else if (pageIndex === pageOptions.length - 1) {
    entriesEnd = rows.length;
  } else {
    entriesEnd = pageSize * (pageIndex + 1);
  }

  const RenderPlaceHolderText = () => {
    let text = [];
    let size = table.columns.length;
    for (let i = 1; i < size; i++) {
      let textToInsert = "Type here";
      let currentType = table.columns[i].type;
      if (currentType === "dropdown") {
        textToInsert = "Choose and Click";
      } else if (currentType === "date") {
        textToInsert = "Choose the Date";
      }

      text.push(
        <DataTableBodyCell key={i}>
          <p className="portfolioInput" onClick={() => toggleStockPopup(true)}>
            {textToInsert}
          </p>
        </DataTableBodyCell>
      );
    }
    return text;
  };

  const handleRowAction = (action, data) => {
    if (action === "close") {
      toggleActionPopup(true);
      setStockToClose(data);
      return;
    }

    setStockToClose(null);
  };

  const handleClosingPriceChange = (event) => {
    const value = event.target.value;
    const stock = { ...stockToClose };
    stock.yourClosingPrice = parseFloat(value);
    setStockToClose(stock);
  };

  const handleClosingDateChange = (event) => {
    const value = event.target.value;
    const stock = { ...stockToClose };
    stock.closeDate = value;
    setStockToClose(stock);
  };

  return (
    <>
      <TableContainer sx={{ boxShadow: "none" }}>
        {entriesPerPage || canSearch ? (
          <div>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
              {entriesPerPage && (
                <SoftBox display="flex" alignItems="center">
                  <SoftSelect
                    defaultValue={{ value: defaultValue, label: defaultValue }}
                    options={entries.map((entry) => ({ value: entry, label: entry }))}
                    onChange={setEntriesPerPage}
                    size="small"
                  />
                  <SoftTypography variant="caption" color="secondary">
                    &nbsp;&nbsp;entries per page
                  </SoftTypography>
                </SoftBox>
              )}
              {canSearch && (
                <SoftBox sx={{ display: "flex", gap: "5px" }} ml="auto">
                  <SoftInput
                    placeholder="Search..."
                    value={search}
                    onChange={({ currentTarget }) => {
                      setSearch(search);
                      onSearchChange(currentTarget.value);
                    }}
                  />
                  <SoftButton
                    variant="contained"
                    color="secondary"
                    onClick={() => toggleStockPopup(true)}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    + Add Stock
                  </SoftButton>
                </SoftBox>
              )}
            </SoftBox>
          </div>
        ) : null}
        {loading ? (
          <div className="loadingIcon">
            <CircularProgress />
          </div>
        ) : (
          <>
            <Table {...getTableProps()}>
              <SoftBox className="bbvLightGrey rowBoxes" component="thead">
                {headerGroups.map((headerGroup, key) => (
                  <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column, key) => (
                      <DataTableHeadCell
                        key={key}
                        {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                        width={column.width ? column.width : "auto"}
                        align="left"
                        sorted={setSortedValue(column)}
                      >
                        {column.render("Header")}
                      </DataTableHeadCell>
                    ))}
                  </TableRow>
                ))}
              </SoftBox>

              <TableBody {...getTableBodyProps()}>
                {page.map((row, pageKey) => {
                  prepareRow(row);
                  return (
                    <TableRow className="alignCenter" key={pageKey} {...row.getRowProps()}>
                      {row.cells.map((cell, key) => (
                        <DataTableBodyCell
                          key={key}
                          className="alignCenter rowBoxes"
                          noBorder={noEndBorder && rows.length - 1 === key}
                          //align used to align the data content
                          // align = {pageKey === page.length - 1?"right":"left"}
                          align="left"
                          {...cell.getCellProps()}
                        >
                          {cell.column.Header === "SYMBOL" ? (
                            <DashboardLink to={"/GEM/stocks-screener/" + cell.value} target="_blank">
                              {cell.render("Cell")}
                            </DashboardLink>
                          ) : cell.column.Header === "ACTION" ? (
                            <div>
                              <ActionDropdown
                                type="stocks"
                                modifyTable={modifyTable}
                                data={data}
                                currentAction={cell.value}
                                rowIndex={pageKey}
                                onChange={handleRowAction}
                              />
                            </div>
                          ) : cell.column.Header === "REMARKS" ? (
                            <TableRemarks type="stocks" rowIndex={pageKey} modifyTable={modifyTable} data={data} />
                          ) : (
                            <div className="leftAlign">
                              <p style={{ maxWidth: "110px" }} className="helvetica portfolioCellText">
                                {cell.render("Cell")}
                              </p>
                            </div>
                          )}
                        </DataTableBodyCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {rows.length > 0 ? (
              <SoftBox
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                p={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
              >
                {showTotalEntries && (
                  <SoftBox mb={{ xs: 3, sm: 0 }}>
                    <SoftTypography variant="button" color="secondary" fontWeight="regular">
                      Showing {entriesStart} to {entriesEnd} of {rows.length} entries
                    </SoftTypography>
                  </SoftBox>
                )}
                {pageOptions.length > 1 && (
                  <SoftPagination
                    variant={pagination.variant ? pagination.variant : "gradient"}
                    color={pagination.color ? pagination.color : "info"}
                  >
                    {canPreviousPage && (
                      <SoftPagination item onClick={() => previousPage()}>
                        <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                      </SoftPagination>
                    )}
                    {renderPagination.length > 6 ? (
                      <SoftBox width="5rem" mx={1}>
                        <SoftInput
                          inputProps={{
                            type: "number",
                            min: 1,
                            max: customizedPageOptions.length,
                          }}
                          value={customizedPageOptions[pageIndex]}
                          onChange={(handleInputPagination, handleInputPaginationValue)}
                        />
                      </SoftBox>
                    ) : (
                      renderPagination
                    )}
                    {canNextPage && (
                      <SoftPagination item onClick={() => nextPage()}>
                        <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                      </SoftPagination>
                    )}
                  </SoftPagination>
                )}
              </SoftBox>
            ) : (
              <SoftBox align="center" my={3}>
                <SoftButton variant="contained" color="secondary" onClick={() => toggleStockPopup(true)}>
                  + Add Stock
                </SoftButton>
              </SoftBox>
            )}
            <SoftBox align="center" mb={3}>
              {isLoading && <CircularProgress />}
            </SoftBox>
          </>
        )}

        {stockPopup && (
          <Popup onScroll={handleScroll}>
            <CloseIcon className="close-btn icon" onClick={cancelCurrentInfo} />
            <div>
              <h1 style={{ textAlign: "center", fontSize: "18px", marginBottom: "20px" }} className="roboto">
                Add Stock
              </h1>

              {initialCapital ? (
                <>
                  <div>
                    <Grid container alignItems="center">
                      <div className="headerBox" style={{ display: "flex", justifyContent: "space-between" }}>
                        <p className="helvetica" style={{ fontSize: "12px" }}>
                          SYMBOL
                        </p>
                        <SearchIcon style={{ marginRight: "12px" }}></SearchIcon>
                      </div>
                      <div className={isConfirmed ? "inputBox" : "searchInputBox"}>
                        {isConfirmed ? (
                          <p className="helvetica" style={{ fontSize: "12px" }}>
                            {inputText.symbol}
                          </p>
                        ) : (
                          <SearchSymbolInput withIcon={false} inputText={inputText} setInputText={setInputText} />
                        )}
                      </div>
                    </Grid>
                    {/* <Grid container alignItems="center">
                <div className="headerBox">
                  <p className="helvetica" style={{ fontSize: "12px" }}>
                    STOCK TYPE
                  </p>
                </div>
                <div className="buttonInputBox">
                  <div>
                    {isConfirmed ? (
                      <p className="helvetica" style={{ fontSize: "12px", paddingLeft: "10px" }}>
                        {inputText.stockType}
                      </p>
                    ) : (
                      <SoftButton
                        id="stock-button"
                        className="portfolioDropdown"
                        aria-controls={openStock ? "index-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={openStock ? "true" : undefined}
                        onClick={handleClick}
                        variant="text"
                      >
                        {currentStock}
                        <ExpandMoreIcon />
                      </SoftButton>
                    )}

                    <Menu
                      id="stock-menu"
                      anchorEl={stockType}
                      open={openStock}
                      onClose={closeStock}
                      style={{ position: "", zIndex: "10000" }}
                      MenuListProps={{
                        "aria-labelledby": "basic-button",
                      }}
                    >
                      <MenuItem onClick={closeStock}>Buy/Long</MenuItem>
                      <MenuItem onClick={closeStock}>Sell/Short</MenuItem>
                    </Menu>
                  </div>
                </div>
              </Grid> */}
                    <Grid container alignItems="center">
                      <div className="headerBox">
                        <p className="helvetica" style={{ fontSize: "12px" }}>
                          TRADE DATE
                        </p>
                      </div>
                      <div className="inputBox">
                        {isConfirmed ? (
                          <p className="helvetica" style={{ fontSize: "12px" }}>
                            {inputText.tradeDate}
                          </p>
                        ) : (
                          <input
                            name="tradeDate"
                            type="date"
                            placeholder="Type here"
                            onChange={handleStockChange}
                            value={inputText.tradeDate}
                            className="underlinedInput"
                          />
                        )}
                      </div>
                    </Grid>
                    <Grid container alignItems="center">
                      <div className="headerBox">
                        <p className="helvetica" style={{ fontSize: "12px" }}>
                          TRADE PRICE
                        </p>
                      </div>
                      <div className="inputBox">
                        {isConfirmed ? (
                          <p className="helvetica" style={{ fontSize: "12px" }}>
                            {inputText.tradePrice}
                          </p>
                        ) : (
                          <input
                            name="tradePrice"
                            type="number"
                            placeholder="Type here"
                            onChange={handleStockChange}
                            value={inputText.tradePrice}
                            className="underlinedInput"
                          />
                        )}
                      </div>
                    </Grid>
                    <Grid container alignItems="center">
                      <div className="headerBox">
                        <p className="helvetica" style={{ fontSize: "12px" }}>
                          QUANTITY
                        </p>
                      </div>
                      <div className="inputBox">
                        {isConfirmed ? (
                          <p className="helvetica" style={{ fontSize: "12px" }}>
                            {inputText.quantity}
                          </p>
                        ) : (
                          <input
                            name="quantity"
                            type="number"
                            placeholder="Type here"
                            onChange={handleStockChange}
                            value={inputText.quantity}
                            className="underlinedInput"
                          />
                        )}
                      </div>
                    </Grid>
                    <Grid container alignItems="center">
                      <div className="headerBox">
                        <p className="helvetica" style={{ fontSize: "12px" }}>
                          CAPITAL
                        </p>
                      </div>
                      <div className="inputBox">
                        <p className="helvetica" style={{ fontSize: "12px" }}>
                          {isNumber(inputText.capital) ? inputText.capital.toFixed(2) : 0}
                        </p>
                      </div>
                    </Grid>
                    <Grid container alignItems="center">
                      <div className="headerBox">
                        <p className="helvetica" style={{ fontSize: "12px" }}>
                          CASH FLOW
                        </p>
                      </div>
                      <div className="inputBox">
                        <p className="helvetica" style={{ fontSize: "12px" }}>
                          {cashFlow.toFixed(2)}
                        </p>
                      </div>
                    </Grid>
                  </div>
                  {isConfirmed ? (
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: "20px" }}>
                      <div>
                        <Button
                          className="greyOutline"
                          variant="outlined"
                          onClick={cancelCurrentInfo}
                          style={{ marginRight: "20px" }}
                          color="info"
                        >
                          Cancel
                        </Button>
                        <Button className="greyOutline" variant="outlined" onClick={confirmButton} color="info">
                          Edit
                        </Button>
                      </div>
                      <Button className="bbvGreen" onClick={sendStockInfo} variant="gradient" color="info">
                        Send
                      </Button>
                    </div>
                  ) : (
                    <div style={{ float: "right", marginTop: "25px" }}>
                      <div>
                        <Button className="greyOutline" variant="outlined" onClick={cancelCurrentInfo}>
                          Cancel
                        </Button>
                        <Button
                          className="bbvGrey"
                          style={{ marginLeft: "20px" }}
                          onClick={confirmButton}
                          variant="gradient"
                          color="info"
                        >
                          Confirm & Send
                        </Button>
                      </div>
                      <SoftTypography
                        variant="body2"
                        align="right"
                        color="error"
                        sx={{ whiteSpace: "pre-line" }}
                        mt={2}
                      >
                        {errorMessage}
                      </SoftTypography>
                    </div>
                  )}
                </>
              ) : (
                <SoftTypography textAlign="center" variant="body2">
                  In order to add stocks to your portfolio,
                  <br />
                  please add initial capital first.
                </SoftTypography>
              )}
            </div>
          </Popup>
        )}

        {actionPopup && (
          <>
            <Popup onScroll={handleScroll}>
              <CloseIcon className="close-btn icon" onClick={cancelCurrentInfo} />
              <div>
                <h1 style={{ textAlign: "center", fontSize: "18px", marginBottom: "20px" }} className="roboto">
                  Close Stock
                </h1>
                <SoftTypography>Are you sure you want to close this stock?</SoftTypography>
                <SoftTypography variant="body2">
                  It will be removed from your portfolio and any loss or gain will be adjusted to your cash flow
                </SoftTypography>
                <Grid container alignItems="center" mt={2}>
                  <div className="headerBox">
                    <p className="helvetica" style={{ fontSize: "12px" }}>
                      Closing
                    </p>
                  </div>
                  <div className="inputBox">
                    {stockToClose.name} ({stockToClose.symbol})
                  </div>
                </Grid>
                <Grid container alignItems="center" mt={2}>
                  <div className="headerBox">
                    <p className="helvetica" style={{ fontSize: "12px" }}>
                      Qty
                    </p>
                  </div>
                  <div className="inputBox">{stockToClose.quantity}</div>
                </Grid>
                <Grid container alignItems="center" mt={2}>
                  <div className="headerBox">
                    <p className="helvetica" style={{ fontSize: "12px" }}>
                      Current Market Price
                    </p>
                  </div>
                  <div className="inputBox">{stockToClose.currentMktPrice}</div>
                </Grid>
                <Grid container alignItems="center" mt={2}>
                  <div className="headerBox">
                    <p className="helvetica" style={{ fontSize: "12px" }}>
                      Your Closing Price
                    </p>
                  </div>
                  <div className="inputBox">
                    <input
                      name="closing_price"
                      type="number"
                      placeholder="Type here"
                      onChange={handleClosingPriceChange}
                      value={stockToClose.yourClosingPrice}
                      className="underlinedInput"
                    />
                  </div>
                </Grid>
                <Grid container alignItems="center" mt={2}>
                  <div className="headerBox">
                    <p className="helvetica" style={{ fontSize: "12px" }}>
                      Close Date
                    </p>
                  </div>
                  <div className="inputBox">
                    <input
                      name="closeDate"
                      type="date"
                      placeholder="Type here"
                      onChange={handleClosingDateChange}
                      value={stockToClose.closeDate}
                      className="underlinedInput"
                    />
                  </div>
                </Grid>
                <Grid container alignItems="center" mt={2}>
                  <div className="headerBox">
                    <p className="helvetica" style={{ fontSize: "12px" }}>
                      P/L
                    </p>
                  </div>
                  <div className="inputBox">
                    {(
                      (stockToClose.yourClosingPrice ? stockToClose.yourClosingPrice : stockToClose.currentMktPrice) *
                        stockToClose.quantity -
                      stockToClose.capital
                    ).toFixed(2)}
                  </div>
                </Grid>

                <SoftBox py={2}>
                  <Button className="bbvGreen" onClick={markStockClosed} variant="gradient" color="info">
                    Yes! Close this Stock
                  </Button>
                  <Button variant="link" onClick={cancelCurrentInfo}>
                    Cancel
                  </Button>
                </SoftBox>
              </div>
            </Popup>
          </>
        )}
      </TableContainer>
    </>
  );
}

// Setting default values for the props of DataTable
PortfolioStockTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
};

// Typechecking props for the DataTable
PortfolioStockTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark", "light"]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
};

export default PortfolioStockTable;
